/**
 * Reusable schedule editor: time + item rows, free text rows, blank spacers.
 * Stored as JSON string: { v: 1, lines: [...] } in calendar tab `text` field.
 */
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  formatScheduleTimeDisplay,
  formatTimeInputMask,
  normalizeSchedulePeriod,
  parseScheduleTimeFields,
} from '../utils/scheduleTimeUtils';

export function newScheduleLineId() {
  return `l_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Default label for the last schedule row when adding at end. */
export const SCHEDULE_LAST_ITEM_DEFAULT = 'bus call';

function newEndScheduleRow() {
  return {
    id: newScheduleLineId(),
    kind: 'schedule',
    time: '',
    period: 'AM',
    item: SCHEDULE_LAST_ITEM_DEFAULT,
    notes: '',
  };
}

/** @typedef {{ id: string, kind: 'unset' } | { id: string, kind: 'schedule', time: string, item: string } | { id: string, kind: 'text', text: string } | { id: string, kind: 'blank' }} ScheduleLine */

/**
 * @param {string} raw
 * @returns {{ lines: ScheduleLine[] }}
 */
export function parseScheduleText(raw) {
  const s = String(raw || '').trim();
  if (!s) return { lines: [] };
  try {
    const o = JSON.parse(s);
    if (o && o.v === 1 && Array.isArray(o.lines)) {
      const lines = o.lines.map((row) => normalizeImportedLine(row)).filter(Boolean);
      return { lines };
    }
  } catch {
    /* fall through */
  }
  if (s) {
    return { lines: [{ id: newScheduleLineId(), kind: 'text', text: String(raw) }] };
  }
  return { lines: [] };
}

function normalizeImportedLine(row) {
  if (!row || typeof row !== 'object') return null;
  const id = String(row.id || '').trim() || newScheduleLineId();
  const kind = row.kind;
  if (kind === 'unset') {
    return { id, kind: 'unset' };
  }
  if (kind === 'schedule') {
    const parsed = parseScheduleTimeFields(row.time, row.period);
    return {
      id,
      kind: 'schedule',
      time: parsed.time,
      period: parsed.period,
      item: String(row.item ?? ''),
      notes: String(row.notes ?? ''),
    };
  }
  if (kind === 'text') {
    return { id, kind: 'text', text: String(row.text ?? '') };
  }
  if (kind === 'blank') {
    return { id, kind: 'blank' };
  }
  return null;
}

/**
 * @param {{ lines: ScheduleLine[] }} doc
 * @returns {string} empty string when no lines (caller may delete doc)
 */
export function serializeScheduleDoc(doc) {
  const lines = Array.isArray(doc?.lines) ? doc.lines : [];
  if (lines.length === 0) return '';
  return JSON.stringify({ v: 1, lines });
}

/** Plain-text preview for day details / PDF */
export function formatScheduleAsPlainText(raw) {
  const { lines } = parseScheduleText(raw);
  const parts = lines
    .map((ln) => {
      if (ln.kind === 'unset') return '';
      if (ln.kind === 'schedule') {
        const t = formatScheduleTimeDisplay(ln.time, ln.period || 'AM');
        const it = String(ln.item || '').trim();
        const notes = String(ln.notes || '').trim();
        if (!t && !it && !notes) return '';
        const head = [t, it].filter(Boolean).join(' — ');
        return notes ? `${head}\n  ${notes}` : head;
      }
      if (ln.kind === 'text') return String(ln.text || '').trim();
      if (ln.kind === 'blank') return '';
      return '';
    })
    .filter((x) => x.length > 0);
  const joined = parts.join('\n');
  if (joined) return joined;
  if (lines.some((l) => l.kind === 'unset')) return 'Draft items';
  return '';
}

/** Live preview while compact composer is open (matches saved-line preview rules). */
function draftPreviewLabel(dKind, dTime, dItem, dText) {
  if (dKind === 'text') {
    const t = String(dText || '').trim();
    return t || '·';
  }
  const ti = String(dTime || '').trim();
  const it = String(dItem || '').trim();
  if (!ti && !it) return '·';
  return `${ti || '…'} — ${it || '…'}`;
}

function previewLabel(ln) {
  if (ln.kind === 'unset') return '·';
  if (ln.kind === 'schedule') {
    const t = String(ln.time || '').trim();
    const it = String(ln.item || '').trim();
    if (!t && !it) return '·';
    return `${t || '…'} — ${it || '…'}`;
  }
  if (ln.kind === 'text') {
    const t = String(ln.text || '').trim();
    return t || '·';
  }
  if (ln.kind === 'blank') return '·';
  return '';
}

/**
 * @param {object} props
 * @param {string} props.value raw JSON or legacy text
 * @param {(next: string) => void} props.onChange
 * @param {'compact' | 'full'} props.mode
 * @param {object} props.theme tokens from useTheme().theme
 * @param {number} [props.fullMaxHeight] when mode is full, max height (px) for editor + footer so it fits in a modal
 * @param {boolean} [props.compactFloat] when compact, float composer beside the day cell instead of stacking
 * @param {'left' | 'right'} [props.compactFloatSide] which side of the cell to dock the floating composer
 * @param {'default' | 'paper'} [props.fullVariant] full-mode visual style
 * @param {'timeItem' | 'itemTime'} [props.fullLayout] full-mode schedule field order
 */
function lineFromComposerDraft(id, dKind, dTime, dItem, dText, dPeriod = 'AM', dNotes = '') {
  if (dKind === 'text') {
    return { id, kind: 'text', text: String(dText ?? '') };
  }
  return {
    id,
    kind: 'schedule',
    time: formatTimeInputMask(dTime),
    period: normalizeSchedulePeriod(dPeriod),
    item: String(dItem ?? ''),
    notes: String(dNotes ?? ''),
  };
}

function SchedulePeriodToggle({ period, onToggle, theme, styles }) {
  const p = normalizeSchedulePeriod(period);
  return (
    <View style={styles.periodToggle}>
      {['AM', 'PM'].map((label) => (
        <TouchableOpacity
          key={label}
          style={[styles.periodBtn, p === label && styles.periodBtnActive]}
          onPress={() => onToggle(label)}
        >
          <Text style={[styles.periodBtnText, p === label && styles.periodBtnTextActive]}>{label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );
}

function SortableScheduleRow({ id, children, disabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.88 : 1,
    zIndex: isDragging ? 2 : 0,
  };
  return (
    <View ref={setNodeRef} style={style}>
      {typeof children === 'function' ? children({ attributes, listeners }) : children}
    </View>
  );
}

function ScheduleInlineRow({ ln, theme, styles, onUpdate, onRemove, dragHandleProps, notesExpanded, onToggleNotes }) {
  if (ln.kind === 'text') {
    return (
      <View style={styles.inlineRow}>
        {dragHandleProps ? (
          <TouchableOpacity style={styles.dragHandle} {...dragHandleProps.listeners} {...dragHandleProps.attributes}>
            <Ionicons name="reorder-three" size={18} color={theme.text.tertiary} />
          </TouchableOpacity>
        ) : null}
        <TextInput
          style={[styles.textIn, styles.textInCompact, { flex: 1 }]}
          value={ln.text}
          onChangeText={(t) => onUpdate({ text: t })}
          placeholder="Text line"
          placeholderTextColor={theme.text.tertiary}
        />
        <TouchableOpacity onPress={onRemove} hitSlop={8}>
          <Ionicons name="trash-outline" size={16} color={theme.status?.error || '#c0392b'} />
        </TouchableOpacity>
      </View>
    );
  }

  const period = ln.period || 'AM';
  const hasNotes = Boolean(String(ln.notes || '').trim());
  const showNotes = notesExpanded || hasNotes;
  return (
    <View style={styles.inlineRowStack}>
      <View style={styles.inlineRow}>
        {dragHandleProps ? (
          <TouchableOpacity style={styles.dragHandle} {...dragHandleProps.listeners} {...dragHandleProps.attributes}>
            <Ionicons name="reorder-three" size={18} color={theme.text.tertiary} />
          </TouchableOpacity>
        ) : null}
        <TextInput
          style={[styles.timeIn, styles.timeInCompact]}
          value={ln.time}
          onChangeText={(t) => onUpdate({ time: formatTimeInputMask(t) })}
          placeholder="10:30"
          placeholderTextColor={theme.text.tertiary}
          keyboardType={Platform.OS === 'web' ? 'default' : 'numeric'}
        />
        <SchedulePeriodToggle
          period={period}
          onToggle={(next) => onUpdate({ period: next })}
          theme={theme}
          styles={styles}
        />
        <TextInput
          style={[styles.itemIn, styles.itemInCompact, { flex: 1 }]}
          value={ln.item}
          onChangeText={(t) => onUpdate({ item: t })}
          placeholder="Item"
          placeholderTextColor={theme.text.tertiary}
        />
        <TouchableOpacity
          style={[styles.notesToggleBtn, showNotes && styles.notesToggleBtnActive]}
          onPress={onToggleNotes}
          hitSlop={6}
          accessibilityLabel={showNotes ? 'Hide description' : 'Add description'}
        >
          <Ionicons
            name={showNotes ? 'chevron-up' : 'document-text-outline'}
            size={14}
            color={showNotes || hasNotes ? theme.brand.primary : theme.text.tertiary}
          />
        </TouchableOpacity>
        <TouchableOpacity onPress={onRemove} hitSlop={8}>
          <Ionicons name="trash-outline" size={16} color={theme.status?.error || '#c0392b'} />
        </TouchableOpacity>
      </View>
      {showNotes ? (
        <TextInput
          style={[styles.notesIn, styles.notesInCompact]}
          value={ln.notes || ''}
          onChangeText={(t) => onUpdate({ notes: t })}
          placeholder="Description (optional)"
          placeholderTextColor={theme.text.tertiary}
          multiline
        />
      ) : null}
    </View>
  );
}

function hydrateComposerDraftFromLine(setters, ln) {
  const { setDKind, setDTime, setDItem, setDText } = setters;
  if (!ln) return;
  if (ln.kind === 'text') {
    setDKind('text');
    setDText(String(ln.text ?? ''));
    setDTime('');
    setDItem('');
    return;
  }
  if (ln.kind === 'blank') {
    setDKind('text');
    setDText('');
    setDTime('');
    setDItem('');
    return;
  }
  /* schedule or unset */
  setDKind('schedule');
  setDTime(ln.kind === 'schedule' ? String(ln.time ?? '') : '');
  setDItem(ln.kind === 'schedule' ? String(ln.item ?? '') : '');
  setDText('');
}

export function ScheduleEditor({
  value,
  onChange,
  mode,
  theme,
  fullMaxHeight,
  compactFloat,
  compactFloatSide = 'right',
  fullVariant = 'default',
  fullLayout = 'timeItem',
  compactFill = false,
  /** When true, rows flow in the parent scroll (sidebar) instead of a nested scroll area. */
  compactUnscroll = false,
  /** When true, full-mode rows expand in the parent scroll instead of a capped inner scroll. */
  fullUnscroll = false,
  /** Compact + smaller box/text (used inside month-view day cells). */
  dense = false,
}) {
  const styles = useMemo(
    () =>
      createStyles(
        theme,
        mode === 'compact',
        fullMaxHeight,
        Boolean(compactFloat),
        fullVariant,
        compactFill,
        compactUnscroll,
        fullUnscroll,
        dense
      ),
    [theme, mode, fullMaxHeight, compactFloat, fullVariant, compactFill, compactUnscroll, fullUnscroll, dense]
  );
  const { lines } = useMemo(() => parseScheduleText(value), [value]);
  const isPaperFull = mode === 'full' && fullVariant === 'paper';
  const [activePaperId, setActivePaperId] = useState(null);
  const [expandedNotesIds, setExpandedNotesIds] = useState(() => new Set());
  /** Compact: add/edit happens in a composer; Save commits (new rows append). */
  const [compactComposer, setCompactComposer] = useState(null);
  const [dKind, setDKind] = useState('schedule');
  const [dTime, setDTime] = useState('');
  const [dItem, setDItem] = useState('');
  const [dText, setDText] = useState('');

  const resetComposerDraft = useCallback(() => {
    setDKind('schedule');
    setDTime('');
    setDItem('');
    setDText('');
  }, []);

  const pushLines = useCallback(
    (nextLines) => {
      onChange(serializeScheduleDoc({ lines: nextLines }));
    },
    [onChange]
  );

  const updateLine = useCallback(
    (id, patch) => {
      const next = lines.map((ln) => {
        if (ln.id !== id) return ln;
        if (ln.kind === 'unset') {
          const time = patch.time !== undefined ? formatTimeInputMask(patch.time) : '';
          const item = patch.item !== undefined ? String(patch.item) : '';
          return {
            id: ln.id,
            kind: 'schedule',
            time,
            period: normalizeSchedulePeriod(patch.period || 'AM'),
            item,
            notes: patch.notes !== undefined ? String(patch.notes) : '',
          };
        }
        if (ln.kind === 'schedule' && patch.time !== undefined) {
          return { ...ln, ...patch, time: formatTimeInputMask(patch.time) };
        }
        return { ...ln, ...patch };
      });
      pushLines(next);
    },
    [lines, pushLines]
  );

  const setLineKind = useCallback(
    (id, kind) => {
      const next = lines.map((ln) => {
        if (ln.id !== id) return ln;
        if (ln.kind === kind) return ln;
        if (kind === 'schedule') {
          return { id: ln.id, kind: 'schedule', time: '', period: 'AM', item: '', notes: '' };
        }
        if (kind === 'blank') return { id: ln.id, kind: 'blank' };
        if (kind === 'text') return { id: ln.id, kind: 'text', text: '' };
        return ln;
      });
      pushLines(next);
    },
    [lines, pushLines]
  );

  const removeLine = useCallback(
    (id) => {
      pushLines(lines.filter((ln) => ln.id !== id));
      setCompactComposer((c) => (c?.mode === 'edit' && c.id === id ? null : c));
      setExpandedNotesIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [lines, pushLines]
  );

  const toggleNotesExpanded = useCallback((id) => {
    setExpandedNotesIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const insertAfter = useCallback(
    (index, line) => {
      const next = [...lines.slice(0, index + 1), line, ...lines.slice(index + 1)];
      pushLines(next);
    },
    [lines, pushLines]
  );

  const moveLine = useCallback(
    (index, delta) => {
      const j = index + delta;
      if (j < 0 || j >= lines.length) return;
      const next = [...lines];
      const t = next[index];
      next[index] = next[j];
      next[j] = t;
      pushLines(next);
    },
    [lines, pushLines]
  );

  const isCompact = mode === 'compact';

  useEffect(() => {
    if (!isCompact || !compactComposer || compactComposer.mode !== 'edit') return;
    if (!lines.some((l) => l.id === compactComposer.id)) {
      setCompactComposer(null);
    }
  }, [isCompact, compactComposer, lines]);

  useEffect(() => {
    if (!isPaperFull) return;
    if (activePaperId && !lines.some((l) => l.id === activePaperId)) {
      setActivePaperId(null);
    }
  }, [activePaperId, isPaperFull, lines]);

  const renderFullLine = (ln, index) => (
    <View key={ln.id}>
      <View
        style={[styles.fullLineCard, fullVariant === 'paper' && styles.fullLineCardPaper]}
        onTouchStart={() => isPaperFull && setActivePaperId(ln.id)}
      >
        {!isPaperFull ? (
          <View style={[styles.fullLineToolbar, fullVariant === 'paper' && styles.fullLineToolbarPaper]}>
            <View style={styles.fullReorderCol}>
              <TouchableOpacity
                style={[styles.fullReorderBtn, index === 0 && styles.fullReorderBtnDisabled]}
                onPress={() => moveLine(index, -1)}
                disabled={index === 0}
                accessibilityLabel="Move up"
              >
                <Ionicons name="chevron-up" size={16} color={theme.text.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.fullReorderBtn, index === lines.length - 1 && styles.fullReorderBtnDisabled]}
                onPress={() => moveLine(index, 1)}
                disabled={index === lines.length - 1}
                accessibilityLabel="Move down"
              >
                <Ionicons name="chevron-down" size={16} color={theme.text.primary} />
              </TouchableOpacity>
            </View>
            <View style={styles.fullToolbarIcons}>
              <TouchableOpacity
                style={[styles.compactIconTypeBtn, ln.kind === 'schedule' && styles.compactIconTypeBtnActive]}
                onPress={() => setLineKind(ln.id, 'schedule')}
                accessibilityLabel="Time and item"
              >
                <Ionicons name="time-outline" size={15} color={theme.text.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.compactIconTypeBtn, ln.kind === 'text' && styles.compactIconTypeBtnActive]}
                onPress={() => setLineKind(ln.id, 'text')}
                accessibilityLabel="Text line"
              >
                <Text style={styles.compactTypeLetter}>T</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.fullToolbarSpacer} />
            <TouchableOpacity
              style={styles.fullToolbarTrash}
              onPress={() => removeLine(ln.id)}
              accessibilityLabel="Remove line"
            >
              <Ionicons name="trash-outline" size={18} color={theme.status?.error || '#c0392b'} />
            </TouchableOpacity>
          </View>
        ) : null}
        {ln.kind === 'unset' ? <Text style={styles.fullUnsetHint}>Choose clock or T above.</Text> : null}
        {ln.kind === 'schedule' ? (
          <View style={styles.inlineRowStack}>
            <View style={[styles.scheduleRow, fullVariant === 'paper' && styles.scheduleRowPaper]}>
              <TextInput
                style={[styles.timeIn, fullVariant === 'paper' && styles.timeInPaper]}
                value={ln.time}
                onChangeText={(t) => updateLine(ln.id, { time: formatTimeInputMask(t) })}
                onFocus={() => isPaperFull && setActivePaperId(ln.id)}
                placeholder="10:30"
                placeholderTextColor={theme.text.tertiary}
              />
              <SchedulePeriodToggle
                period={ln.period || 'AM'}
                onToggle={(next) => updateLine(ln.id, { period: next })}
                theme={theme}
                styles={styles}
              />
              <TextInput
                style={[styles.itemIn, fullVariant === 'paper' && styles.itemInPaper, { flex: 1 }]}
                value={ln.item}
                onChangeText={(t) => updateLine(ln.id, { item: t })}
                onFocus={() => isPaperFull && setActivePaperId(ln.id)}
                placeholder="Item"
                placeholderTextColor={theme.text.tertiary}
              />
              {!isPaperFull ? (
                <TouchableOpacity
                  style={[
                    styles.notesToggleBtn,
                    (expandedNotesIds.has(ln.id) || String(ln.notes || '').trim()) && styles.notesToggleBtnActive,
                  ]}
                  onPress={() => toggleNotesExpanded(ln.id)}
                  hitSlop={6}
                  accessibilityLabel={
                    expandedNotesIds.has(ln.id) || String(ln.notes || '').trim()
                      ? 'Hide description'
                      : 'Add description'
                  }
                >
                  <Ionicons
                    name={expandedNotesIds.has(ln.id) || String(ln.notes || '').trim() ? 'chevron-up' : 'document-text-outline'}
                    size={14}
                    color={
                      expandedNotesIds.has(ln.id) || String(ln.notes || '').trim()
                        ? theme.brand.primary
                        : theme.text.tertiary
                    }
                  />
                </TouchableOpacity>
              ) : null}
            </View>
            {!isPaperFull && (expandedNotesIds.has(ln.id) || String(ln.notes || '').trim()) ? (
              <TextInput
                style={[styles.notesIn, fullVariant === 'paper' && styles.notesInPaper]}
                value={ln.notes || ''}
                onChangeText={(t) => updateLine(ln.id, { notes: t })}
                onFocus={() => isPaperFull && setActivePaperId(ln.id)}
                placeholder="Description (optional)"
                placeholderTextColor={theme.text.tertiary}
                multiline
              />
            ) : null}
            {isPaperFull ? (
              <TextInput
                style={[styles.notesIn, fullVariant === 'paper' && styles.notesInPaper]}
                value={ln.notes || ''}
                onChangeText={(t) => updateLine(ln.id, { notes: t })}
                onFocus={() => isPaperFull && setActivePaperId(ln.id)}
                placeholder="Description (optional)"
                placeholderTextColor={theme.text.tertiary}
                multiline
              />
            ) : null}
          </View>
        ) : null}
        {ln.kind === 'text' ? (
          <TextInput
            style={[styles.textIn, fullVariant === 'paper' && styles.textInPaper]}
            value={ln.text}
            onChangeText={(t) => updateLine(ln.id, { text: t })}
            onFocus={() => isPaperFull && setActivePaperId(ln.id)}
            placeholder="Text (leave empty for a blank line)"
            placeholderTextColor={theme.text.tertiary}
          />
        ) : null}
        {ln.kind === 'blank' ? (
          <View style={styles.fullBlankBody}>
            <View style={styles.fullBlankRule} />
            <Text style={styles.fullBlankCaption}>Spacer (legacy). Tap T to edit as text.</Text>
          </View>
        ) : null}
        {isPaperFull && activePaperId === ln.id ? (
          <View style={styles.paperActivePanel}>
            <TouchableOpacity
              style={[styles.paperActiveBtn, index === 0 && styles.fullReorderBtnDisabled]}
              onPress={() => moveLine(index, -1)}
              disabled={index === 0}
              accessibilityLabel="Move up"
            >
              <Ionicons name="chevron-up" size={14} color={theme.text.primary} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.paperActiveBtn, index === lines.length - 1 && styles.fullReorderBtnDisabled]}
              onPress={() => moveLine(index, 1)}
              disabled={index === lines.length - 1}
              accessibilityLabel="Move down"
            >
              <Ionicons name="chevron-down" size={14} color={theme.text.primary} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.paperActiveBtn, ln.kind === 'schedule' && styles.compactIconTypeBtnActive]}
              onPress={() => setLineKind(ln.id, 'schedule')}
              accessibilityLabel="Time and item"
            >
              <Ionicons name="time-outline" size={14} color={theme.text.primary} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.paperActiveBtn, ln.kind === 'text' && styles.compactIconTypeBtnActive]}
              onPress={() => setLineKind(ln.id, 'text')}
              accessibilityLabel="Text line"
            >
              <Text style={styles.compactTypeLetter}>T</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.paperActiveBtn}
              onPress={() => insertAfter(index, { id: newScheduleLineId(), kind: 'schedule', time: '', item: '' })}
              accessibilityLabel="Insert line below"
            >
              <Ionicons name="add" size={14} color={theme.brand.accent} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.paperActiveBtn}
              onPress={() => removeLine(ln.id)}
              accessibilityLabel="Remove line"
            >
              <Ionicons name="trash-outline" size={14} color={theme.status?.error || '#c0392b'} />
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
      {!isPaperFull ? (
        <TouchableOpacity
          style={[styles.fullInsertBetween, fullVariant === 'paper' && styles.fullInsertBetweenPaper]}
          onPress={() => insertAfter(index, { id: newScheduleLineId(), kind: 'schedule', time: '', item: '' })}
          accessibilityLabel="Insert line below"
        >
          <Ionicons name="add" size={15} color={theme.brand.accent} />
        </TouchableOpacity>
      ) : null}
    </View>
  );

  if (isCompact) {
    const useDnD = Platform.OS === 'web' && !compactFloat;
    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
      useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const addScheduleRow = () => {
      pushLines([...lines, newEndScheduleRow()]);
    };

    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = lines.findIndex((l) => l.id === active.id);
      const newIndex = lines.findIndex((l) => l.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      pushLines(arrayMove(lines, oldIndex, newIndex));
    };

    const renderLine = (ln, dragProps = null) => (
      <ScheduleInlineRow
        key={ln.id}
        ln={ln}
        theme={theme}
        styles={styles}
        onUpdate={(patch) => updateLine(ln.id, patch)}
        onRemove={() => removeLine(ln.id)}
        dragHandleProps={dragProps}
        notesExpanded={expandedNotesIds.has(ln.id)}
        onToggleNotes={() => toggleNotesExpanded(ln.id)}
      />
    );

    const listInner = useDnD ? (
      <SortableContext items={lines.map((l) => l.id)} strategy={verticalListSortingStrategy}>
        {lines.map((ln) => (
          <SortableScheduleRow key={ln.id} id={ln.id}>
            {(dragProps) => renderLine(ln, dragProps)}
          </SortableScheduleRow>
        ))}
      </SortableContext>
    ) : (
      lines.map((ln) => renderLine(ln))
    );

    const listBody = useDnD ? (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {listInner}
      </DndContext>
    ) : (
      listInner
    );

    const listScroll = compactUnscroll ? (
      <View style={styles.scrollCompactUnscroll}>{listBody}</View>
    ) : (
      <ScrollView
        style={[styles.scrollCompact, compactFill && styles.scrollCompactFill]}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {listBody}
      </ScrollView>
    );

    return (
      <View style={[styles.wrap, compactFloat && styles.compactFloatingHost]}>
        {lines.length > 0 ? listScroll : (
          <Text style={[styles.compactPreview, { paddingVertical: 8 }]}>No schedule items yet.</Text>
        )}
        <TouchableOpacity style={styles.compactPlusBtn} onPress={addScheduleRow} accessibilityLabel="Add schedule item">
          <Ionicons name="add" size={13} color={theme.brand.primary} />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      {fullUnscroll ? (
        <View style={styles.scrollFullUnscroll}>{lines.map((ln, index) => renderFullLine(ln, index))}</View>
      ) : (
        <ScrollView
          style={styles.scrollFull}
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {lines.map((ln, index) => renderFullLine(ln, index))}
        </ScrollView>
      )}
      <TouchableOpacity
        style={[styles.fullFooterAdd, fullVariant === 'paper' && styles.fullFooterAddPaper]}
        onPress={() => pushLines([...lines, newEndScheduleRow()])}
        accessibilityLabel="Add line at end"
      >
        <Ionicons name="add-circle-outline" size={20} color={theme.brand.primary} />
        <Text style={styles.fullFooterAddText}>Add line</Text>
      </TouchableOpacity>
    </View>
  );
}

function createStyles(
  theme,
  compact,
  fullMaxHeight,
  compactFloatHost,
  fullVariant,
  compactFill = false,
  compactUnscroll = false,
  fullUnscroll = false,
  dense = false
) {
  const d = (regular, denseVal) => (dense ? denseVal : regular);
  const footerReserve = 72;
  const scrollFullMax =
    compact ? 200 : typeof fullMaxHeight === 'number' ? Math.max(120, fullMaxHeight - footerReserve) : 480;
  const scrollCompactStyle = compactUnscroll
    ? { width: '100%', minWidth: 0 }
    : compactFill
      ? { flex: 1, minHeight: 88 }
      : dense
        ? { flex: 1, minHeight: 0 }
        : { maxHeight: compact ? (compactFloatHost ? 52 : 88) : 200 };
  return StyleSheet.create({
    wrap: {
      width: '100%',
      minWidth: 0,
      ...(dense && !compactUnscroll ? { flex: 1, minHeight: 0 } : {}),
      ...(compactFill && !compactUnscroll ? { flex: 1, minHeight: 0 } : {}),
      ...(typeof fullMaxHeight === 'number' && !compact
        ? { maxHeight: fullMaxHeight, overflow: 'hidden' }
        : {}),
    },
    compactFloatingHost: {
      position: 'relative',
      overflow: 'visible',
      width: '100%',
      zIndex: 2,
    },
    compactFloatingAnchor: { width: '100%', overflow: 'visible' },
    compactComposerFloat: {
      position: 'absolute',
      bottom: 0,
      width: 216,
      maxWidth: 220,
      zIndex: 50,
      ...(Platform.OS === 'android' ? { elevation: 10 } : {}),
      ...(Platform.OS === 'web'
        ? { boxShadow: `0 4px 20px ${theme.ui.shadowStrong || 'rgba(0,0,0,0.18)'}` }
        : {}),
    },
    compactComposerFloatRight: { left: '100%', marginLeft: 6 },
    compactComposerFloatLeft: { right: '100%', marginRight: 6 },
    scrollCompact: scrollCompactStyle,
    scrollCompactFill: scrollCompactStyle,
    scrollCompactUnscroll: scrollCompactStyle,
    inlineRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginBottom: 6,
    },
    inlineRowStack: {
      marginBottom: 8,
      gap: 4,
    },
    dragHandle: {
      paddingVertical: 4,
      paddingHorizontal: 2,
      ...(Platform.OS === 'web' ? { cursor: 'grab' } : {}),
    },
    periodToggle: {
      flexDirection: 'row',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      overflow: 'hidden',
    },
    periodBtn: {
      paddingHorizontal: d(8, 6),
      paddingVertical: d(6, 4),
      backgroundColor: theme.background.secondary,
    },
    periodBtnActive: {
      backgroundColor: theme.brand.primary,
    },
    periodBtnText: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.secondary,
    },
    periodBtnTextActive: {
      color: theme.button?.primaryText || '#fff',
    },
    notesIn: {
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      fontSize: 12,
      color: theme.text.primary,
      backgroundColor: theme.input?.background || theme.background.secondary,
      minHeight: 36,
    },
    notesInCompact: {
      marginLeft: d(28, 22),
      fontSize: d(11, 10),
      minHeight: d(32, 26),
      paddingVertical: d(6, 4),
    },
    notesToggleBtn: {
      width: 26,
      height: 26,
      borderRadius: 7,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    notesToggleBtnActive: {
      borderColor: theme.brand.primary,
      backgroundColor: theme.name === 'dark' ? 'rgba(46, 204, 113, 0.12)' : 'rgba(39, 174, 96, 0.1)',
    },
    compactEmptyPreviewSlot: {
      minHeight: 30,
      marginBottom: 4,
      paddingHorizontal: 6,
      paddingVertical: 4,
      borderRadius: 6,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
      justifyContent: 'center',
    },
    compactRowDraft: {
      borderStyle: 'dashed',
      opacity: 0.92,
    },
    scrollFull: { maxHeight: fullUnscroll ? undefined : scrollFullMax },
    scrollFullUnscroll: { width: '100%', minWidth: 0 },
    compactRowWrap: { marginBottom: 4 },
    compactComposerCard: {
      marginTop: d(4, 3),
      marginBottom: d(6, 4),
      paddingHorizontal: d(6, 5),
      paddingVertical: d(6, 4),
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
      ...(Platform.OS === 'android' ? { elevation: 2 } : {}),
    },
    compactComposerCardFloating: {
      marginTop: 0,
      marginBottom: 0,
      ...(Platform.OS === 'android' ? { elevation: 12 } : {}),
      ...(Platform.OS === 'web' ? { boxShadow: `0 6px 24px ${theme.ui.shadowStrong || 'rgba(0,0,0,0.2)'}` } : {}),
    },
    compactComposerTypeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginBottom: 6,
    },
    compactIconTypeBtn: {
      width: d(28, 22),
      height: d(28, 22),
      borderRadius: 7,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
    compactIconTypeBtnActive: {
      borderColor: theme.brand.primary,
      backgroundColor: theme.name === 'dark' ? 'rgba(46, 204, 113, 0.12)' : 'rgba(39, 174, 96, 0.1)',
    },
    compactTypeLetter: {
      fontSize: d(14, 12),
      fontWeight: '900',
      color: theme.text.primary,
      marginTop: -1,
    },
    compactComposerActions: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      marginTop: 6,
      gap: 6,
    },
    compactComposerActionsFlex: { flex: 1, minWidth: 4 },
    compactComposerBtnPrimary: {
      paddingHorizontal: 12,
      paddingVertical: 5,
      borderRadius: 7,
      backgroundColor: theme.button.primary,
    },
    compactComposerBtnPrimaryText: { fontSize: 11, fontWeight: '800', color: theme.button.primaryText },
    compactComposerBtnGhost: { paddingHorizontal: 8, paddingVertical: 5 },
    compactComposerBtnGhostText: { fontSize: 11, fontWeight: '700', color: theme.text.secondary },
    compactComposerBtnDanger: { paddingHorizontal: 8, paddingVertical: 5 },
    compactComposerBtnDangerText: { fontSize: 11, fontWeight: '800', color: theme.status?.error || '#c0392b' },
    compactRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: d(3, 2),
      paddingHorizontal: d(5, 4),
      borderRadius: 6,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    compactRowActive: {
      borderColor: theme.brand.primary,
      backgroundColor: theme.name === 'dark' ? 'rgba(46, 204, 113, 0.12)' : 'rgba(39, 174, 96, 0.1)',
    },
    compactPreview: { flex: 1, fontSize: d(10, 9), fontWeight: '700', color: theme.text.primary },
    compactPlusBtn: {
      alignSelf: 'center',
      marginTop: 2,
      width: 20,
      height: 20,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.brand.primary,
      backgroundColor: theme.background.card,
    },
    fullLineCard: {
      paddingHorizontal: 10,
      paddingVertical: 10,
      marginBottom: 2,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
      ...(Platform.OS === 'android' ? { elevation: 1 } : {}),
      boxShadow: Platform.OS === 'web' ? `0 1px 4px ${theme.ui.shadow || 'rgba(0,0,0,0.06)'}` : undefined,
    },
    fullLineCardPaper: {
      borderRadius: 6,
      borderWidth: 1,
      borderColor: '#000000',
      backgroundColor: 'transparent',
      paddingHorizontal: 6,
      paddingVertical: 3,
      marginBottom: 2,
      ...(Platform.OS === 'android' ? { elevation: 0 } : {}),
      boxShadow: undefined,
    },
    fullLineToolbar: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
      gap: 8,
    },
    fullLineToolbarPaper: { marginBottom: 2 },
    fullReorderCol: { flexDirection: 'column', justifyContent: 'center', marginRight: 2 },
    fullReorderBtn: { paddingVertical: 2, paddingHorizontal: 4, minWidth: 28, alignItems: 'center' },
    fullReorderBtnDisabled: { opacity: 0.28 },
    fullToolbarIcons: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    fullToolbarSpacer: { flex: 1 },
    fullToolbarTrash: { padding: 4 },
    fullUnsetHint: { fontSize: 11, color: theme.text.tertiary, marginBottom: 4 },
    fullBlankBody: { marginTop: 2 },
    fullBlankRule: {
      height: 1,
      backgroundColor: theme.ui.border,
      marginVertical: 6,
      borderRadius: 1,
    },
    fullBlankCaption: { fontSize: 10, color: theme.text.tertiary },
    fullInsertBetween: {
      alignSelf: 'center',
      paddingVertical: 6,
      paddingHorizontal: 12,
      marginBottom: 4,
    },
    fullInsertBetweenPaper: { paddingVertical: 3, marginBottom: 2 },
    fullFooterAdd: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      marginTop: 10,
      paddingVertical: 12,
      paddingHorizontal: 14,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderStyle: 'dashed',
      backgroundColor: theme.background.card,
    },
    fullFooterAddPaper: {
      marginTop: 10,
      borderRadius: 0,
      borderStyle: 'solid',
      borderColor: '#000000',
      backgroundColor: '#ffffff',
    },
    fullFooterAddText: { fontSize: 14, fontWeight: '800', color: theme.brand.primary },
    scheduleRow: { flexDirection: 'row', alignItems: 'center', gap: 4, minWidth: 0, width: '100%' },
    scheduleRowPaper: { gap: 4 },
    timeIn: {
      width: 72,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 6,
      paddingHorizontal: 6,
      paddingVertical: 4,
      fontSize: 12,
      color: theme.input.text,
      backgroundColor: theme.input.background,
    },
    timeInPaper: {
      borderRadius: 0,
      borderColor: '#000000',
      backgroundColor: 'transparent',
      color: '#000000',
      textAlign: 'right',
      fontWeight: '700',
      fontSize: 14,
      paddingVertical: 2,
    },
    timeInCompact: { width: d(56, 46), fontSize: d(11, 10), paddingVertical: d(3, 2) },
    dash: { fontSize: 11, color: theme.text.tertiary, fontWeight: '700' },
    itemIn: {
      flex: 1,
      minWidth: 0,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 6,
      paddingHorizontal: 6,
      paddingVertical: 4,
      fontSize: 12,
      color: theme.input.text,
      backgroundColor: theme.input.background,
    },
    itemInPaper: {
      borderRadius: 0,
      borderWidth: 0,
      borderBottomWidth: 0,
      backgroundColor: 'transparent',
      color: '#000000',
      fontWeight: '700',
      textTransform: 'uppercase',
      fontSize: 14,
      paddingVertical: 2,
    },
    itemInCompact: { fontSize: d(11, 10), paddingVertical: d(3, 2) },
    textIn: {
      flex: 1,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 6,
      paddingHorizontal: 6,
      paddingVertical: 4,
      fontSize: 12,
      color: theme.input.text,
      backgroundColor: theme.input.background,
    },
    textInCompact: { fontSize: 11, paddingVertical: 3 },
    textInPaper: {
      borderWidth: 0,
      borderRadius: 0,
      backgroundColor: 'transparent',
      color: '#000000',
      fontWeight: '700',
      textTransform: 'uppercase',
      fontSize: 14,
      paddingVertical: 2,
    },
    paperActivePanel: {
      position: 'absolute',
      top: -20,
      right: 6,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 6,
      paddingVertical: 4,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.borderStrong || theme.ui.border,
      backgroundColor: theme.background.elevated,
      ...(Platform.OS === 'android' ? { elevation: 6 } : {}),
      ...(Platform.OS === 'web' ? { boxShadow: `0 6px 18px ${theme.ui.shadowStrong || 'rgba(0,0,0,0.18)'}` } : {}),
      zIndex: 20,
    },
    paperActiveBtn: {
      width: 24,
      height: 24,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
    },
  });
}
